import { all, call, put, select } from 'redux-saga/effects';
import {
  requestConfirmationDocument,
  requestConfirmationDocuments,
  requestFacilityDefaultTemplate,
  requestMergeTokenList,
  requestTemplates,
  updateClientConfirmationDocStatus,
} from './saga';
import { PayloadAction } from '@reduxjs/toolkit';
import { trackException } from 'app-insights/appInsightsTracking';
import { IConfirmationDoc } from './types';
import { pConfirmationDocEditorActions } from './slice';
import { selectDefaultTemplate, selectDocumentView, selectPageAttributes, selectSelectedTemplate } from './selector';
import { ITemplate } from '../confirmation-template-editor/types';
import { instanceOfConfirmationDoc } from './utils';
import { ExceptionType } from 'app/enums/Common';
import queryString from 'query-string';
import { placementStatusAction } from '../placement-status/slice';
import { placementSummaryActions } from '../placement-summary/slice';
import { selectPlacementHeaderData, selectPlacementOrderType } from '../placement-details/selectors';
import { Concatenate } from 'utils/string/string';
import { base64DocumentToURL } from 'app/helpers/fileHelper';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import i18next from 'i18next';
import { DocSource } from '../placement-candidate-contract-doc-editor/types';

export function* onInit(action: PayloadAction<{ placementId: number }>) {
  try {
    const {
      default_selection: defaultSelection,
      documentId: docIdfromUrl,
      source,
    } = queryString.parse(window.location.search);

    const [templates, { draftDoc, documents }, defaultFacilityTemplateId, , fetchedDocFromUrl] = yield all([
      call(requestTemplates),
      call(requestConfirmationDocuments, action),
      call(requestFacilityDefaultTemplate, action),
      call(requestMergeTokenList),
      ...(docIdfromUrl
        ? [
            call(requestConfirmationDocument, {
              payload: { documentId: docIdfromUrl, source },
              type: pConfirmationDocEditorActions.getConfirmationDoc.type,
            }),
          ]
        : []),
    ]);

    const defaultFacilityTemplate = templates.find(x => x.templateId === defaultFacilityTemplateId);
    const docFromUrl = documents.find(x => x.documentId === docIdfromUrl);

    const docSelection =
      !!docIdfromUrl && fetchedDocFromUrl
        ? /** Code: If the document is fetched as per url params */
          docFromUrl ?? null
        : defaultSelection === 'draft' && draftDoc
        ? /** Code: If the defaultSelection is draft and its exists */ draftDoc
        : documents.length === 0 && defaultFacilityTemplate !== undefined
        ? /** Code: If neither draft nor any docs exists */ { ...defaultFacilityTemplate, forceReset: true }
        : null;
    yield put(pConfirmationDocEditorActions.setSelectedTemplate(docSelection));
    yield put(
      pConfirmationDocEditorActions.setSidePanel(
        /** Code: Display templates only if there are neither docs nor draft exists */
        documents.length > 0 && (docSelection === null || instanceOfConfirmationDoc(docSelection))
          ? 'documents'
          : 'templates',
      ),
    );
  } catch (exception) {
    trackException({
      exception,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'onInit',
        area: 'src/store/redux-store/placement-client-confirmation-doc-editor/business.saga',
      },
    });
  }
}

export function* onConfirmationDocClick(action: PayloadAction<{ card: ITemplate | IConfirmationDoc | null }>) {
  try {
    const { card } = action.payload;
    const { renderPdfView, renderPreviewScreen } = yield select(selectDocumentView);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('default_selection')) urlParams.delete('default_selection');

    if (card?.isDraft || !instanceOfConfirmationDoc(card)) {
      urlParams.delete('documentId');
      urlParams.delete('source');
      if (renderPdfView || renderPreviewScreen) yield put(pConfirmationDocEditorActions.resetDocView());

      /** Code: Reset hard delete prop on choosing current draft */
      if (card?.isDraft && instanceOfConfirmationDoc(card))
        yield put(pConfirmationDocEditorActions.markCurrentDraftForHardDelete(false));

      yield put(pConfirmationDocEditorActions.setSelectedTemplate(card));
    } else {
      const callStatus = yield call(requestConfirmationDocument, {
        payload: {
          documentId: card.source === DocSource.classic ? card.documentImageId : card.documentId,
          source: card.source,
        },
        type: pConfirmationDocEditorActions.getConfirmationDoc.type,
      });
      if (callStatus) {
        urlParams.set('documentId', card.documentId);
        urlParams.set('source', card.source);
        yield put(pConfirmationDocEditorActions.setSelectedTemplate(card));
      }
    }
    window.history.pushState(null, null, Concatenate([window.location.pathname, urlParams.toString()], '?'));
  } catch (exception) {
    trackException({
      exception,
      properties: {
        functionName: 'onConfirmationDocClick',
        area: 'src/store/redux-store/placement-client-confirmation-doc-editor/business.saga',
      },
    });
  } finally {
    /**
     * Code: resetting dirty state since publishConfirmationDocTokens shall change the state to dirty
     * but editor state is reset within component by setSelectedTemplate
     */
    yield call(resetEditorDirtyState);
  }
}

export function* switchToDefaultTemplateDraftView() {
  try {
    yield put(pConfirmationDocEditorActions.setSidePanel('templates'));
    yield put(pConfirmationDocEditorActions.resetDocView());

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('default_selection')) urlParams.delete('default_selection');
    if (urlParams.has('documentId')) {
      urlParams.delete('documentId');
      urlParams.delete('source');
    }

    window.history.pushState(null, null, Concatenate([window.location.pathname, urlParams.toString()], '?'));

    const defaultTemplate = yield select(selectDefaultTemplate);
    yield put(pConfirmationDocEditorActions.setSelectedTemplate(defaultTemplate ?? null));
  } catch (exception) {
    trackException({
      exception,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'generateDraft',
        area: 'src/store/redux-store/placement-client-confirmation-doc-editor/business.saga',
      },
    });
  }
}

export function* closeConfirmationEditor() {
  try {
    const placementOrderType = yield select(selectPlacementOrderType);
    const { placementId } = yield select(selectPlacementHeaderData);
    /** Code: Refresh WhatsNext Container */
    yield put(placementStatusAction.getPlacementStatusDetails({ placementId }));
    yield put(placementSummaryActions.getBookingRequirement({ placementId, placementOrderType }));

    // const searchParams = new URLSearchParams(window.location.search);
    // searchParams.delete('view');
    // searchParams.delete('default_selection');
    // searchParams.delete('documentId');
    // searchParams.delete('source');

    // window.history.pushState(null, null, Concatenate([window.location.pathname, searchParams.toString()], '?'));
  } catch (exception) {
    trackException({
      exception,
      properties: {
        functionName: 'closeConfirmationEditor',
        area: 'src/store/redux-store/placement-client-confirmation-doc-editor/business.saga',
      },
    });
  }
}

export function* downloadSelectedConfirmationDoc() {
  try {
    const { document } = yield select(selectDocumentView);
    if (document?.documentId) {
      const { dispose } = base64DocumentToURL(document.data, {
        fileType: 'application/pdf',
        fileName: document.fileName,
        download: true,
      });
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.downloadSuccess'),
          severity: 'success',
        }),
      );
      dispose();
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('templateEditor.placement.snackbar.contractEditor.downloadFailure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'downloadSelectedConfirmationDoc',
        area: 'src/store/redux-store/placement-client-confirmation-doc-editor/business.saga',
      },
    });
  }
}

export function* updateConfirmationDocStatusToSent(
  action: PayloadAction<{
    placementId: number;
    documentId: string;
  }>,
) {
  try {
    const callStatus = yield call(updateClientConfirmationDocStatus, {
      ...action,
      type: pConfirmationDocEditorActions.updateClientConfirmationDocStatus.type,
    });
    if (callStatus) {
      yield put(pConfirmationDocEditorActions.toggleForceModalCloseFlag());
      const { documents } = yield call(requestConfirmationDocuments, {
        payload: { placementId: action.payload.placementId },
        type: action.type,
      });
      const updatedDocument = documents.find(doc => doc.documentId === action.payload.documentId);
      const selectedDoc = yield select(selectSelectedTemplate);
      if (selectedDoc?.documentId === updatedDocument?.documentId)
        yield put(pConfirmationDocEditorActions.setSelectedTemplate(updatedDocument));
    }
  } catch (exception) {
    trackException({
      exception,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'updateConfirmationDocStatusToSent',
        area: 'src/store/redux-store/placement-client-confirmation-doc-editor/business.saga',
      },
    });
  }
}

export function* resetEditorDirtyState() {
  try {
    const { forceEditorDirtyState } = yield select(selectPageAttributes);
    if (forceEditorDirtyState) yield put(pConfirmationDocEditorActions.forceEditorDirtyState(false));
  } catch (exception) {
    trackException({
      exception,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'resetEditorDirtyState',
        area: 'src/store/redux-store/placement-client-confirmation-doc-editor/business.saga',
      },
    });
  }
}
